import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Anchor, ArrowRight, LogIn } from "lucide-react";
import logoLight from "../assets/antaryatra-logo-horizontal 3.0.svg";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import { useAuth } from "../context/AuthContext";

export default function JoinGateway() {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { enterAsGuest } = useAuth(); // NEW
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const isGuestMode = query.get("mode") === "guest";

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("token")) {
            // Recognize the reflection and skip the choice
            navigate("/signup-flow" + location.search, { 
                replace: true,
                state: location.state 
            });
        }
    }, [location.search, navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-serenity-blue-50 to-serenity-purple-50 dark:from-gray-900 dark:to-gray-800 font-body flex flex-col">
            <SEO 
              title="Join Antaryatra" 
              description="Begin your journey. Choose to seek a quiet mind or guide others as a listener in our sanctuary." 
              url="/signup" 
            />
            {/* Navbar Minimal */}
            <nav className="p-6 flex justify-between items-center max-w-7xl mx-auto w-full">
                <img src={logoLight} alt="Antaryatra" className="h-16 w-auto object-contain" />
                <button
                    onClick={() => navigate("/login")}
                    className="text-sm font-medium text-gray-600 hover:text-brand-blue flex items-center gap-2"
                >
                    <LogIn className="w-4 h-4" /> {t('auth_login_btn')}
                </button>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center p-6 pb-20">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center max-w-2xl mx-auto mb-12 space-y-4"
                >
                    <h1 className="text-4xl md:text-5xl font-display font-medium text-gray-900 dark:text-white leading-tight">
                        {t('gateway_title')}
                    </h1>
                    <p className="text-lg text-gray-500 dark:text-gray-400">
                        {t('gateway_sub')}
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6 w-full max-w-4xl">
                    {/* Card 1: Seeker */}
                    <PathCard
                        title={t('gateway_seeker_path')}
                        desc={t('gateway_seeker_desc')}
                        icon={Compass}
                        color="bg-brand-blue text-white"
                        onClick={() => {
                            if (isGuestMode) {
                                enterAsGuest("SEEKER");
                                navigate("/dashboard");
                            } else {
                                navigate("/signup-flow", { state: location.state });
                            }
                        }}
                        delay={0.1}
                        cta={isGuestMode ? "Enter as Guest Seeker" : t('gateway_cta')}
                    />

                    {/* Card 2: Guide */}
                    <PathCard
                        title={t('gateway_guide_path')}
                        desc={t('gateway_guide_desc')}
                        icon={Anchor}
                        color="bg-brand-orange text-white"
                        onClick={() => {
                            if (isGuestMode) {
                                enterAsGuest("PROVIDER");
                                navigate("/dashboard");
                            } else {
                                navigate("/apply");
                            }
                        }}
                        delay={0.2}
                        cta={isGuestMode ? "Enter as Guest Guide" : t('gateway_cta')}
                    />
                </div>
            </div>
        </div>
    );
}

function PathCard({ title, desc, icon: Icon, color, onClick, delay, cta }) {
    return (
        <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay }}
            onClick={onClick}
            className="group relative p-8 rounded-3xl bg-white dark:bg-gray-800 shadow-xl shadow-gray-200/50 dark:shadow-none border border-white/50 dark:border-gray-700 hover:scale-[1.02] transition-all text-left flex flex-col h-full"
        >
            <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center mb-6 shadow-lg`}>
                <Icon className="w-7 h-7" />
            </div>

            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-brand-blue transition-colors">
                {title}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-8 flex-1">
                {desc}
            </p>

            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                {cta} <ArrowRight className="w-4 h-4" />
            </div>
        </motion.button>
    );
}
